import * as d3 from 'd3';
import { throttle } from '../utils';
import axios from 'axios';
import CountySelector from './CountySelector';
import Sidebar from './Sidebar';
import Observable from '../utils/Observable';
import countyTemplate from '../templates/countyTemplate';

class UKMap {
  constructor(el) {
    this.observable = new Observable();
    this.container = el;
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.selector = new CountySelector(this.observable);
    this.sidebar = new Sidebar(this.observable, countyTemplate);
    this.counties;

    this.svg;
    this.countiesGeo;
    this.countriesGeo;
    this.centered;
    this.path;
    this.g;
    this.countiesData;

    this.rateById = d3.map();
    this.quantize = d3.scaleQuantize()
      .domain([0, 9])
      .range(d3.range(9).map(function(i) { return "q" + i + "-9"; }));

    this.svg = d3.select(map)
      .append("svg")
      .attr("class", "svg")
      .attr('preserveAspectRatio', 'xMidYMid')
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('xlink', 'http://www.w3.org/1999/xlink')
      .attr('version', '1.1');

    this.resize = this.resize.bind(this);
    this.clicked = this.clicked.bind(this);
    this.selectCounty = this.selectCounty.bind(this);

    this.observable.subscribe('sidebar-closed', this.clicked);
    this.observable.subscribe('location-selected', this.selectCounty);

    this.init();
  }


  init() {
    // load all the data
    axios.all([this.getCountiesGeoJson(), this.getCountriesGeoJson(), this.getCountiesData()])
      .then(axios.spread((countiesGeo, countriesGeo, countiesData) => {
        this.countiesGeo = countiesGeo.data;
        this.countriesGeo = countriesGeo.data;
        this.countiesData = countiesData.data.data;

        this.counties = this.generateCountiesList(this.countiesGeo.features);
        this.selector.init(this.counties);
        this.draw();
        this.bindEventListeners();
        this.container.classList.add('is-visible');
        document.body.classList.add('is-map-visible');
      }))
      .catch(err => {
        console.log('data error', err);
      });
  }


  bindEventListeners() {
    window.addEventListener('resize', throttle(this.resize), 1000);
  }


  generateCountiesList(features) {
    // create a array and sort alphabetically
    return features.map((feature) => {
      return {
        'id': feature.properties.ID_2,
        'name': feature.properties.NAME_2,
        'country': feature.properties.NAME_1
      };
    }).sort((a,b) => {
      if(a.name < b.name) return -1;
      if(a.name > b.name) return 1;
      return 0;
    });
  }


  draw() {
    // create a unit projection
    let projection = d3.geoMercator()
      .scale(1)
      .translate([0, 0]);

    // create a path generator.
    this.path = d3.geoPath()
      .projection(projection);

    // compute bounds of a point of interest, then derive scale and translate
    // use countriesGeo to center map on england, wales and northern ireland
    let b = this.path.bounds(this.countiesGeo),
      s = .95 / Math.max((b[1][0] - b[0][0]) / this.width, (b[1][1] - b[0][1]) / this.height),
      t = [(this.width - s * (b[1][0] + b[0][0])) / 2, (this.height - s * (b[1][1] + b[0][1])) / 2];

    // update the projection to use computed scale and translate....
    projection
      .scale(s)
      .translate(t);

    // remove if group already exists
    this.g = d3.select('svg g');
    if(this.g) this.g.remove();

    // set svg dimensions
    this.svg.attr("width", this.width)
      .attr("height", this.height)
      .attr('viewBox', `0 0 ${this.width} ${this.height}`);

    // create group
    this.g = this.svg.append('g')
      .attr('class', 'counties');

    // draw the svg of both the geojson and bounding box
    this.g.selectAll("path")
      .data(this.countiesGeo.features)
      .enter()
      .append("path")
      .attr("d", this.path)
      .attr('class', (d) => {
        const countyId = d.properties.ID_2;
        const countyInfo = this.getCountyInfo(countyId);
        const deaths = countyInfo.length > 0 ? countyInfo[0].numberOfDeaths : 0;
        const c = this.countiesData ? this.quantize(deaths) : '';
        const providedInfo = countyInfo.length > 0 && countyInfo[0].providedInformation ? '' : 'no-data-provided';

        return `county ${c} ${providedInfo}`;
      })
      .on('click', this.clicked);
  }


  clicked(d) {
    var x, y, k, w;

    if(d && this.centered !== d) {
      let countyInfo = this.getCountyInfo(d.properties.ID_2);

      // get the center of the feature
      var centroid = this.path.centroid(d);
      x = centroid[0];
      y = centroid[1];
      k = 5;
      w = this.width / 2; // 1/3
      this.centered = d; // set feature as centered object

      this.selector.update(d.properties.ID_2);
      
      this.sidebar.update(countyInfo[0]);
      this.sidebar.open();
    } else {
      x = this.width / 2;
      y = this.height / 2;
      k = 1;
      w = this.width / 2;
      this.centered = null;

      this.selector.reset();
      this.sidebar.close();
    }

    // check if current clicked element is already active
    let same = (d) => d === this.centered;

    this.g.selectAll('path')
      .classed('active', this.centered && same);

    const activeEl = document.querySelector('.county.active');

    if(activeEl) {
      activeEl.parentNode.appendChild(activeEl);
    }

    this.g.transition()
      .duration(500)
      .ease(d3.easeQuadOut)
      .attr("transform", `translate(${w}, ${this.height / 2})scale(${k})translate(${-x}, ${-y})`)
      .style("stroke-width", 1 / k + "px");
  }


  selectCounty(id) {
    const selected = this.countiesGeo.features.filter((county) => {
      return county.properties.ID_2 === parseInt(id);
    });

    this.clicked(selected[0]);
  }

  getCountyInfo(id) {
    return this.countiesData.filter((item) => {
      return item.id === id;
    });
  }

  getCountiesGeoJson() {
    return axios.get('/assets/geojson/uk_counties_unitary.json');
  }

  getCountiesData() {
    return axios.get('/api/counties');
  }

  // center the content on england, wales, northern ireland
  getCountriesGeoJson() {
    return axios.get('/assets/geojson/countries_merged.json');
  }

  resize() {
    // only resize on chanfge of width to avoid resize on scroll
    if(this.container.offsetWidth != this.width) {
      this.width = this.container.offsetWidth;
      this.height = this.container.offsetHeight;
      // redraw the svg
      this.draw();
    }
  }
}

export default UKMap;